"use client";

// Single source of truth for what a "Start free"-class CTA does.
//
// These are the top-of-funnel monetization entry points (nav, footer, pricing,
// mobile menus). They used to focus the homepage hero prompt instead of opening
// signup, so a visitor who had already shown intent got a text box rather than
// an account — and on /pricing the focus target was a DOM id that no longer
// exists under the v15 homepage, so the click did nothing visible at all.
//
// The rule now:
//   • signed out → open the signup modal immediately, carrying any typed prompt
//   • signed in  → route into the app (never re-pitch the public homepage)
//
// Keeping the branch here rather than at each call site is what stops the two
// from drifting again: every surface passes its own `openSignup` / `navigate`
// (each page owns its modal state) but the decision itself is shared.

import { trackStartFreeCtaClicked } from "@/lib/analytics";
import { saveHomePromptDraft } from "@/lib/home-prompt-stash";

/**
 * The public surface a Start-free click came from. Used verbatim as the
 * `source` property on every event in the funnel, so PostHog can break
 * conversion down per CTA.
 */
export type StartFreeSource =
  | "home_nav"
  | "home_footer"
  | "home_mobile_menu"
  | "pricing_nav"
  | "pricing_nav_mobile"
  | "pricing_mobile_menu"
  | "pricing_free_tier";

/**
 * Where a signed-in user lands. Their projects list is the "appropriate
 * logged-in destination" for a CTA that means *start something* — it is the
 * one screen that both shows existing work and offers a new build.
 */
export const SIGNED_IN_START_FREE_HREF = "/projects";

/**
 * Above this length a typed prompt is NOT carried in the URL.
 *
 * `next` is re-encoded into the Supabase email-confirmation redirect
 * (`/auth/confirm?next=…`), so a long prompt gets encoded twice and can blow
 * past redirect-URL limits. Past the cap we deliberately hand off to the
 * localStorage draft stash instead, which holds up to 10k chars and restores
 * on the next homepage mount. Truncating into the URL would be worse than
 * omitting: a non-empty box suppresses the draft restore, so the user would
 * get the clipped copy and permanently lose the rest.
 */
const PREFILL_URL_MAX_CHARS = 400;

/**
 * The post-signup destination for a signed-out Start-free click.
 *
 * `?prefill=` fills the hero box and focuses it WITHOUT submitting — the right
 * behavior for a CTA click, where the user never asked to build yet. (The hero
 * *submit* path keeps its own `?prompt=…&pid=…` handoff, which does auto-build
 * and mints a project id; reusing that here would start a build — and charge
 * credits — off a click the user only meant as "make me an account".)
 */
export function startFreeSignupNext(prompt?: string): string {
  const trimmed = prompt?.trim() ?? "";
  if (!trimmed || trimmed.length > PREFILL_URL_MAX_CHARS) return "/";
  return `/?prefill=${encodeURIComponent(trimmed)}`;
}

export type StartFreeClickOptions = {
  source: StartFreeSource;
  signedIn: boolean;
  /** Opens the page's own signup modal with the given `next` path. */
  openSignup: (next: string) => void;
  /** Navigates a signed-in user into the app. */
  navigate: (href: string) => void;
  /** Hero prompt text, when the surface has one to preserve. */
  prompt?: string;
  /** Override the signed-in destination (defaults to the projects list). */
  signedInHref?: string;
};

/** Run the Start-free CTA behavior for one click. */
export function handleStartFreeClick({
  source,
  signedIn,
  openSignup,
  navigate,
  prompt,
  signedInHref = SIGNED_IN_START_FREE_HREF,
}: StartFreeClickOptions): void {
  const trimmed = prompt?.trim() ?? "";
  const hasPrompt = trimmed.length > 0;

  if (signedIn) {
    trackStartFreeCtaClicked({
      source,
      is_logged_in: true,
      destination: "signed_in_app",
      has_prompt: hasPrompt,
    });
    navigate(signedInHref);
    return;
  }

  // Belt-and-braces alongside the `?prefill=` passthrough: the homepage's own
  // draft writer is debounced by 300ms, so text typed immediately before the
  // click may not have been persisted yet. This also covers the OAuth path,
  // where the provider redirect tears the page down and drops `next`.
  if (hasPrompt) saveHomePromptDraft(trimmed);

  trackStartFreeCtaClicked({
    source,
    is_logged_in: false,
    destination: "signup",
    has_prompt: hasPrompt,
  });
  openSignup(startFreeSignupNext(trimmed));
}
